import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { CareerPathService } from '../../../../core/services/career-path.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CareerPath } from '../../../../core/models/career-path';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PracticesStateService } from '../../practices/practices-state.service';
import { Practice } from '../../../../core/models/practice';

@Component({
  selector: 'em-career-path-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    ButtonModule,
    ToastModule,
    TranslatePipe,
  ],
  templateUrl: './career-path-form.component.html',
  styleUrl: './career-path-form.component.scss',
  providers: [MessageService],
})
export class CareerPathFormComponent {
  form!: FormGroup;
  isEditMode: boolean = false;
  errorMessages: any = {
    practice: 'layout.configurations.careerPath.addCareerPath.practiceIsRequired',
    name: 'layout.configurations.careerPath.addCareerPath.nameIsRequired',
    numberOfLevels: 'layout.configurations.careerPath.addCareerPath.numberOfLevelsIsRequired',
  };

  translations: any = {};
  originalPath!: CareerPath;
  practiceOptions: { label: string; value: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private careerPathService: CareerPathService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
    private practicesState: PracticesStateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.practicesState.practices$.subscribe((practices: Practice[]) => {
      this.practiceOptions = practices.map((p) => ({ label: p.name, value: p.name }));
    });

    this.form = this.fb.group({
      id: [null],
      practice: [null, Validators.required],
      name: [null, Validators.required],
      numberOfLevels: [null, Validators.required],
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.originalPath = { ...this.config.data }; 
      this.populateForm(this.config.data);
    }
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.careerPath.addCareerPath.numberOfLevelsIsRequired',
        'layout.configurations.careerPath.addCareerPath.nameIsRequired',
        'layout.configurations.careerPath.addCareerPath.successMessage.detail',
        'layout.configurations.careerPath.editCareerPath.successMessage.detail',
        'layout.configurations.careerPath.editCareerPath.successMessage.title',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(path: CareerPath) {
    this.form.patchValue({
      id: path.id,
      practice: path.practice,
      name: path.name,
      numberOfLevels: path.numberOfLevels,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateCareerPath();
    } else {
      this.addCareerPath();
    }
  }

  addCareerPath() {
    this.careerPathService.addCareerPath(this.form.value).subscribe({
      next: (path: CareerPath) => {
        this.ref.close(path);
      },
      
      error: (error) => {
        let message: string;

        if (error.ok == false) {
          message = error.error.message + '.';
        } else {
          message = this.translations['layout.configurations.careerPath.addCareerPath.successMessage.detail'];
        }

        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.toast.error'),
          detail: message,
        });
      },
      
    });

  }

  updateCareerPath() {
    const updated = this.form.value;
    const isChanged = JSON.stringify(this.originalPath) !== JSON.stringify(updated);
     if (!isChanged) {
      this.ref.close({ type: 'no-change' });
      return;
    }
    this.careerPathService.updateCareerPath(updated).subscribe({
      next: (path: CareerPath) => {
        this.ref.close({ type: 'updated', data: path });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.toast.error'),
          detail: error.error.message + '.'
        });
      },
    });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);
    return control?.invalid && (control?.dirty || control?.touched);
  }
}
